import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import FilterDatepicker from '../scheduleView/FilterDatepicker';
import '../scheduleView/scheduleView.css';
import './teamsPlanning.css';
import {
  DATE_PRESET_OPTIONS,
  DEFAULT_DATE_PRESET,
  DEFAULT_WORKING_DAYS,
  PRETENDED_HOURS_OPTIONS,
  STORAGE_KEYS,
  WEEKDAY_CHIPS,
  getDefaultDateRange,
  getDateRangeForPreset,
  readStoredValue,
  writeStoredValue,
} from './constants';
import ExportReportModal from './ExportReportModal';

function TopbarDropdown({
  id,
  microLabel,
  icon,
  title,
  menuTitle,
  options,
  value,
  onChange,
  formatValue = (v) => String(v),
  openDropdownId,
  setOpenDropdownId,
  searchable = false,
  searchPlaceholder,
  className = '',
}) {
  const { t } = useTranslation('planning');
  const isOpen = openDropdownId === id;
  const resolvedSearchPlaceholder = searchPlaceholder || t('filterOptions');
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpenDropdownId(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenDropdownId(null);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, setOpenDropdownId]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      return;
    }
    if (!searchable) return;
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, searchable]);

  const optionText = (option) =>
    option.label ||
    (option.labelKey ? t(option.labelKey) : null) ||
    (option.hours != null ? t('hoursCount', { count: option.hours }) : null) ||
    String(option.value ?? '');

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => optionText(option).toLowerCase().includes(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, searchQuery, t]);

  const toggleMenu = () => setOpenDropdownId(isOpen ? null : id);

  return (
    <div
      ref={rootRef}
      id={id}
      className={`topbar-dropdown${isOpen ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        className="fu-filter-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={title}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleMenu();
        }}
      >
        <span className="material-symbols-outlined fu-filter-icon">{icon}</span>
        <span className="fu-filter-micro hidden xl:inline">{microLabel}</span>
        <span className="fu-filter-value">{formatValue(value)}</span>
        <span className="material-symbols-outlined fu-filter-chevron">expand_more</span>
      </button>
      <div className="topbar-dropdown-menu" role="listbox" aria-label={title}>
        <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#617289]">
          {menuTitle}
        </div>
        {searchable && (
          <div className="topbar-dropdown-search-wrap">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              placeholder={resolvedSearchPlaceholder}
              className="topbar-dropdown-search"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
        {filteredOptions.length === 0 && (
          <div className="topbar-dropdown-empty">{t('noMatchingOptions')}</div>
        )}
        {filteredOptions.map((option) => {
          const isSelected = String(option.value) === String(value);
          return (
            <button
              key={String(option.value)}
              type="button"
              className={`topbar-dropdown-item${isSelected ? ' is-selected' : ''}`}
              role="option"
              aria-selected={isSelected}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(option.value);
                setOpenDropdownId(null);
              }}
            >
              <span>{optionText(option)}</span>
              <span className="material-symbols-outlined">check</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function displayOptionLabel(options, value, t) {
  const match = options.find((option) => String(option.value) === String(value));
  if (!match) return value === 'All' ? t('common:all') : String(value);
  if (match.value === 'All') return t('common:all');
  if (match.label) return match.label;
  if (match.labelKey) return t(match.labelKey);
  if (match.hours != null) return t('hoursCount', { count: match.hours });
  return String(match.value);
}

/** Inline filter controls for AdminTopBar `leftExtra`. */
export function TeamsPlanningFilters({
  datePreset,
  startDate,
  endDate,
  onDatePresetChange,
  onStartChange,
  onEndChange,
  pretendedHours,
  onPretendedHoursChange,
  workingDays,
  onWorkingDaysChange,
}) {
  const { t } = useTranslation(['planning', 'common']);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  return (
    <div className="teams-planning-page flex items-center gap-2 min-w-0 flex-1 overflow-visible">
      <div className="flex items-center gap-2 shrink-0">
        <TopbarDropdown
          id="date-preset-dropdown"
          microLabel={t('common:date')}
          icon="calendar_month"
          title={t('filterByDateRange')}
          menuTitle={t('dateRange')}
          options={DATE_PRESET_OPTIONS}
          value={datePreset}
          onChange={onDatePresetChange}
          formatValue={(v) => displayOptionLabel(DATE_PRESET_OPTIONS, v, t)}
          openDropdownId={openDropdownId}
          setOpenDropdownId={setOpenDropdownId}
        />
        <AnimatePresence initial={false}>
          {datePreset === 'custom' && (
            <motion.div
              key="custom-date-range"
              className="filter-custom-date-anim"
              initial={{ width: 0, opacity: 0 }}
              animate={{
                width: 'auto',
                opacity: 1,
                transition: {
                  width: { duration: 0.26, ease: [0.4, 0, 0.2, 1] },
                  opacity: { duration: 0.2, delay: 0.2, ease: 'easeOut' },
                },
              }}
              exit={{
                width: 0,
                opacity: 0,
                transition: {
                  opacity: { duration: 0.12, ease: 'easeIn' },
                  width: { duration: 0.22, delay: 0.08, ease: [0.4, 0, 0.2, 1] },
                },
              }}
            >
              <FilterDatepicker
                startDate={startDate}
                endDate={endDate}
                onStartChange={onStartChange}
                onEndChange={onEndChange}
                onOpenChange={(isOpen) => {
                  if (isOpen) setOpenDropdownId(null);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="topbar-filter-divider hidden md:block" aria-hidden="true" />

      <TopbarDropdown
        id="pretended-hours-dropdown"
        microLabel={t('hrsPerDay')}
        icon="schedule"
        title={t('pretendedHoursPerDay')}
        menuTitle={t('pretendedHrsDay')}
        options={PRETENDED_HOURS_OPTIONS}
        value={pretendedHours}
        onChange={onPretendedHoursChange}
        formatValue={(v) => `${String(v).replace(/\.0$/, '')}h`}
        openDropdownId={openDropdownId}
        setOpenDropdownId={setOpenDropdownId}
      />

      <div className="working-days-chips" title={t('workingDaysOfWeek')}>
        <span className="working-days-chips-label">{t('days')}</span>
        <div className="working-days-chips-row">
          {WEEKDAY_CHIPS.map((day) => {
            const active = workingDays.split(',').includes(String(day.index));
            return (
              <button
                key={day.index}
                type="button"
                className={`working-day-chip${active ? ' is-active' : ''}`}
                onClick={() => onWorkingDaysChange(day.index)}
                aria-pressed={active}
              >
                {t(day.labelKey)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function isBlankFilter(value) {
  return value == null || value === '' || value === 'All';
}

function memberMatchesScope(member, centre, team) {
  if (!member) return false;
  if (!isBlankFilter(centre) && String(member.centre_id ?? '') !== String(centre)) {
    return false;
  }
  if (!isBlankFilter(team) && String(member.team_id ?? '') !== String(team)) {
    return false;
  }
  return true;
}

function filterMembersByScope(members, centre, team) {
  return (members || []).filter((item) => memberMatchesScope(item, centre, team));
}

function isTeamInCentre(teamItem, centre) {
  if (isBlankFilter(centre)) return true;
  const centreIds = Array.isArray(teamItem?.centre_ids) ? teamItem.centre_ids : [];
  return centreIds.some((id) => String(id) === String(centre));
}

/** Shared label + search + chips row (Centre / Team / Member under the top menu). */
function ChipFilterRow({
  label,
  value,
  onChange,
  items = [],
  allValue = 'All',
  allLabel,
  searchPlaceholder,
  emptyLabel,
  ariaLabel,
}) {
  const [query, setQuery] = useState('');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const chipsRef = useRef(null);

  const options = useMemo(() => {
    const mapped = (items || []).map((item) => ({
      value: String(item.id),
      label: item.name,
    }));
    return [{ value: allValue, label: allLabel }, ...mapped];
  }, [allLabel, allValue, items]);

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((option) =>
      String(option.label || '').toLowerCase().includes(normalized)
    );
  }, [options, query]);

  const updateScrollState = () => {
    const node = chipsRef.current;
    if (!node) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const maxScroll = node.scrollWidth - node.clientWidth;
    setCanScrollLeft(node.scrollLeft > 2);
    setCanScrollRight(maxScroll - node.scrollLeft > 2);
  };

  useEffect(() => {
    const node = chipsRef.current;
    if (!node) return undefined;

    updateScrollState();
    const handleScroll = () => updateScrollState();
    node.addEventListener('scroll', handleScroll, { passive: true });

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateScrollState());
      resizeObserver.observe(node);
    } else {
      window.addEventListener('resize', updateScrollState);
    }

    return () => {
      node.removeEventListener('scroll', handleScroll);
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener('resize', updateScrollState);
    };
  }, [filteredOptions]);

  const scrollChips = (direction) => {
    const node = chipsRef.current;
    if (!node) return;
    const amount = Math.max(160, Math.floor(node.clientWidth * 0.7));
    node.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <div className="teams-planning-page chip-filter-row">
      <div className="chip-filter-row-inner">
        <span className="chip-filter-label">{label}</span>
        <div className="chip-filter-search">
          <span className="material-symbols-outlined chip-filter-search-icon">search</span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="chip-filter-search-input"
          />
        </div>
        <div className="chip-filter-chips-wrap">
          <button
            type="button"
            className={`chip-filter-scroll-btn chip-filter-scroll-btn--left${canScrollLeft ? ' is-visible' : ''}`}
            aria-label={`Scroll ${label} options left`}
            tabIndex={canScrollLeft ? 0 : -1}
            disabled={!canScrollLeft}
            onClick={() => scrollChips(-1)}
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <div
            ref={chipsRef}
            className="chip-filter-chips hide-scrollbar"
            role="listbox"
            aria-label={ariaLabel}
          >
            {filteredOptions.length === 0 ? (
              <span className="chip-filter-empty">{emptyLabel}</span>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`chip-filter-chip${isSelected ? ' is-selected' : ''}`}
                    onClick={() => onChange?.(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })
            )}
          </div>
          <button
            type="button"
            className={`chip-filter-scroll-btn chip-filter-scroll-btn--right${canScrollRight ? ' is-visible' : ''}`}
            aria-label={`Scroll ${label} options right`}
            tabIndex={canScrollRight ? 0 : -1}
            disabled={!canScrollRight}
            onClick={() => scrollChips(1)}
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Centre chip row rendered under the top menu. */
export function TeamsPlanningCentreFilterRow({
  centre,
  onCentreChange,
  centres = [],
}) {
  const { t } = useTranslation(['planning', 'common']);
  return (
    <ChipFilterRow
      label={t('centre')}
      value={centre}
      onChange={onCentreChange}
      items={centres}
      allLabel={t('allCentres')}
      searchPlaceholder={t('filterCentres')}
      emptyLabel={t('noCentresMatch')}
      ariaLabel={t('centreFilter')}
    />
  );
}

/** Team chip row rendered under the centre filter. */
export function TeamsPlanningTeamFilterRow({
  team,
  onTeamChange,
  teams = [],
  centre = 'All',
}) {
  const teamsForCentre = useMemo(() => {
    if (isBlankFilter(centre)) return teams;
    return (teams || []).filter((item) => isTeamInCentre(item, centre));
  }, [centre, teams]);

  const { t } = useTranslation(['planning', 'common']);
  return (
    <ChipFilterRow
      label={t('common:team')}
      value={team}
      onChange={onTeamChange}
      items={teamsForCentre}
      allLabel={t('allTeams')}
      searchPlaceholder={t('filterTeams')}
      emptyLabel={t('noTeamsMatch')}
      ariaLabel={t('teamFilter')}
    />
  );
}

/** Member chip row rendered under the team filter. */
export function TeamsPlanningMemberFilterRow({
  member,
  onMemberChange,
  members = [],
  centre = 'All',
  team = 'All',
}) {
  const membersForScope = useMemo(
    () => filterMembersByScope(members, centre, team),
    [centre, members, team]
  );

  const { t } = useTranslation('planning');
  return (
    <ChipFilterRow
      label={t('member')}
      value={member}
      onChange={onMemberChange}
      items={membersForScope}
      allLabel={t('allMembers')}
      searchPlaceholder={t('filterMembers')}
      emptyLabel={t('noMembersMatch')}
      ariaLabel={t('memberFilter')}
    />
  );
}

export function TeamsPlanningExportButton({ filters = {} }) {
  const { t } = useTranslation('planning');
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <div className="teams-planning-page">
      <button
        type="button"
        className="fu-export-btn"
        title={t('exportReport')}
        onClick={() => setExportOpen(true)}
      >
        <span className="material-symbols-outlined text-[18px]">download</span>
        <span className="hidden sm:inline">{t('exportReportTitle')}</span>
      </button>
      <ExportReportModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        filters={filters}
      />
    </div>
  );
}

/** Icon toggle for centre / team / member chip filter rows. */
export function TeamsPlanningPeopleFiltersToggle({
  expanded,
  onToggle,
}) {
  const { t } = useTranslation('planning');
  return (
    <div className="teams-planning-page">
      <button
        type="button"
        className={`people-filters-toggle${expanded ? ' is-expanded' : ''}`}
        onClick={onToggle}
        aria-pressed={expanded}
        aria-label={expanded ? t('hidePeopleFilters') : t('showPeopleFilters')}
        title={expanded ? t('hideFilters') : t('showFilters')}
      >
        <span className="material-symbols-outlined">
          {expanded ? 'filter_alt_off' : 'filter_alt'}
        </span>
      </button>
    </div>
  );
}

/** Animated wrapper for centre / team / member filter rows. */
export function TeamsPlanningPeopleFiltersPanel({
  expanded,
  children,
}) {
  return (
    <AnimatePresence initial={false}>
      {expanded ? (
        <motion.div
          key="people-filters-panel"
          className="teams-planning-page people-filters-panel"
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: 'auto',
            opacity: 1,
            transition: {
              height: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.2, delay: 0.12, ease: 'easeOut' },
            },
          }}
          exit={{
            height: 0,
            opacity: 0,
            transition: {
              opacity: { duration: 0.12, ease: 'easeIn' },
              height: { duration: 0.24, delay: 0.06, ease: [0.4, 0, 0.2, 1] },
            },
          }}
        >
          <div className="people-filters-panel-inner">{children}</div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function useTeamsPlanningFilters() {
  const initialPreset = (() => {
    const stored = readStoredValue(STORAGE_KEYS.datePreset, DEFAULT_DATE_PRESET);
    return DATE_PRESET_OPTIONS.some((option) => option.value === stored)
      ? stored
      : DEFAULT_DATE_PRESET;
  })();
  const defaults = initialPreset === 'custom'
    ? getDefaultDateRange()
    : getDateRangeForPreset(initialPreset);
  const [datePreset, setDatePresetState] = useState(initialPreset);
  const [startDate, setStartDateState] = useState(defaults.startDate);
  const [endDate, setEndDateState] = useState(defaults.endDate);
  const [team, setTeam] = useState(() => readStoredValue(STORAGE_KEYS.team, 'All'));
  const [member, setMember] = useState(() => readStoredValue(STORAGE_KEYS.member, 'All'));
  const [centre, setCentre] = useState(() => readStoredValue(STORAGE_KEYS.centre, 'All'));
  const [pretendedHours, setPretendedHours] = useState(() => {
    const stored = readStoredValue(STORAGE_KEYS.pretendedHours, '8');
    const parsed = Number(stored);
    return Number.isNaN(parsed) ? 8 : parsed;
  });
  const [workingDays, setWorkingDaysState] = useState(() => {
    return readStoredValue(STORAGE_KEYS.workingDays, DEFAULT_WORKING_DAYS);
  });
  const [filterOptions, setFilterOptions] = useState({
    teams: [],
    centres: [],
    members: [],
  });
  const [peopleFiltersExpanded, setPeopleFiltersExpandedState] = useState(() => {
    const stored = readStoredValue(STORAGE_KEYS.peopleFiltersExpanded, 'true');
    return stored !== 'false';
  });

  return {
    datePreset,
    startDate,
    endDate,
    setDatePreset: (value) => {
      const nextPreset = DATE_PRESET_OPTIONS.some((option) => option.value === value)
        ? value
        : DEFAULT_DATE_PRESET;
      setDatePresetState(nextPreset);
      writeStoredValue(STORAGE_KEYS.datePreset, nextPreset);
      if (nextPreset !== 'custom') {
        const range = getDateRangeForPreset(nextPreset);
        setStartDateState(range.startDate);
        setEndDateState(range.endDate);
      }
    },
    setStartDate: (value) => {
      setStartDateState(value);
      setDatePresetState('custom');
      writeStoredValue(STORAGE_KEYS.datePreset, 'custom');
    },
    setEndDate: (value) => {
      setEndDateState(value);
      setDatePresetState('custom');
      writeStoredValue(STORAGE_KEYS.datePreset, 'custom');
    },
    team,
    member,
    centre,
    pretendedHours,
    peopleFiltersExpanded,
    filterOptions,
    setFilterOptions,
    setPeopleFiltersExpanded: (value) => {
      const next = typeof value === 'function' ? value(peopleFiltersExpanded) : Boolean(value);
      setPeopleFiltersExpandedState(next);
      writeStoredValue(STORAGE_KEYS.peopleFiltersExpanded, next ? 'true' : 'false');
    },
    togglePeopleFiltersExpanded: () => {
      setPeopleFiltersExpandedState((prev) => {
        const next = !prev;
        writeStoredValue(STORAGE_KEYS.peopleFiltersExpanded, next ? 'true' : 'false');
        return next;
      });
    },
    setTeam: (value) => {
      setTeam(value);
      writeStoredValue(STORAGE_KEYS.team, value);
      setMember((currentMember) => {
        if (currentMember === 'All') return currentMember;
        const members = filterOptions.members || [];
        const stillValid = members.some(
          (item) =>
            String(item.id) === String(currentMember) &&
            memberMatchesScope(item, centre, value)
        );
        if (stillValid) return currentMember;
        writeStoredValue(STORAGE_KEYS.member, 'All');
        return 'All';
      });
    },
    setMember: (value) => {
      setMember(value);
      writeStoredValue(STORAGE_KEYS.member, value);
    },
    setCentre: (value) => {
      setCentre(value);
      writeStoredValue(STORAGE_KEYS.centre, value);

      let nextTeam = team;
      if (!isBlankFilter(team) && !isBlankFilter(value)) {
        const stillValidTeam = (filterOptions.teams || []).some(
          (item) =>
            String(item.id) === String(team) && isTeamInCentre(item, value)
        );
        if (!stillValidTeam) nextTeam = 'All';
      }
      if (nextTeam !== team) {
        setTeam(nextTeam);
        writeStoredValue(STORAGE_KEYS.team, nextTeam);
      }

      setMember((currentMember) => {
        if (currentMember === 'All') return currentMember;
        const stillValidMember = (filterOptions.members || []).some(
          (item) =>
            String(item.id) === String(currentMember) &&
            memberMatchesScope(item, value, nextTeam)
        );
        if (stillValidMember) return currentMember;
        writeStoredValue(STORAGE_KEYS.member, 'All');
        return 'All';
      });
    },
    setPretendedHours: (value) => {
      setPretendedHours(value);
      writeStoredValue(STORAGE_KEYS.pretendedHours, value);
    },
    workingDays,
    setWorkingDays: (dayIndex) => {
      setWorkingDaysState((prev) => {
        const current = new Set(prev.split(',').filter(Boolean).map(Number));
        if (current.has(dayIndex)) {
          current.delete(dayIndex);
        } else {
          current.add(dayIndex);
        }
        if (current.size === 0) current.add(dayIndex);
        const next = Array.from(current).sort((a, b) => a - b).join(',');
        writeStoredValue(STORAGE_KEYS.workingDays, next);
        return next;
      });
    },
    apiFilters: {
      date_from: startDate,
      date_to: endDate,
      team,
      member,
      centre,
      hours_per_day: pretendedHours,
      working_days: workingDays,
    },
  };
}
